// Game rules and time math shared by server and app. Pure functions, no dependencies.
// Numbers follow SPEC.md §5 and §8 (third round, option A: one level route).
import type { CharId, RouteStop, StopType, LevelInfo, Bonus, LaterWhen, Reward } from './types';

// ---------------------------------------------------------------- points and levels
export const POINTS = { base: 10, lit: 20, onTime: 5, pastStuck: 5, backToBack: 5, perfect: 30, weekChest: 50, firstCard: 10 };
export const ON_TIME_WINDOW_MIN = 15;      // started within 15 min of the card's time
export const BACK_TO_BACK_MIN = 30;        // started within 30 min of the previous card being done
export const PERFECT_MIN_DONE = 3;         // perfect day needs at least 3 done
export const WEEK_CHEST_MIN_LIT = 3;       // week chest opens when at least 3 days were lit that week
export const FREE_MESSAGES_PER_DAY = 30;
export const FLAME_STEPS = [7, 30, 100];

/** Points needed to REACH level L. */
export const reachOf = (L: number): number => (L <= 18 ? (L - 1) * (40 + 10 * L) : 3740 + (L - 18) * 400);

export function levelOf(points: number): LevelInfo {
  let L = 1;
  while (reachOf(L + 1) <= points) L++;
  return { level: L, into: points - reachOf(L), need: reachOf(L + 1) - reachOf(L) };
}

/**
 * Bonus rules for a finished card. Times are epoch ms; cardAt is the card's planned local time as epoch ms.
 * - Lit today: first card done today.
 * - On time: started within 15 min (either side) of the card's planned time.
 * - Got past stuck: Stuck was used on this card.
 * - Back to back: started within 30 min after the previous card of today was done.
 */
export function bonusesFor(o: { litBefore: boolean; startedAt: number; cardAt: number; usedStuck: boolean; prevDoneAt: number | null }): Bonus[] {
  const b: Bonus[] = [];
  if (!o.litBefore) b.push({ label: 'Lit today', pts: POINTS.lit });
  if (Math.abs(o.startedAt - o.cardAt) <= ON_TIME_WINDOW_MIN * 60000) b.push({ label: 'On time', pts: POINTS.onTime });
  if (o.usedStuck) b.push({ label: 'Got past stuck', pts: POINTS.pastStuck });
  if (o.prevDoneAt != null && o.startedAt - o.prevDoneAt >= 0 && o.startedAt - o.prevDoneAt <= BACK_TO_BACK_MIN * 60000)
    b.push({ label: 'Back to back', pts: POINTS.backToBack });
  return b;
}

/** Perfect day: every card in today's plan is done or moved (by talking/Later), none left to do, at least 3 done. */
export function isPerfect(cards: { st: string }[]): boolean {
  const active = cards.filter((c) => c.st !== 'removed');
  const done = active.filter((c) => c.st === 'done').length;
  const todo = active.filter((c) => c.st === 'todo').length;
  return todo === 0 && done >= PERFECT_MIN_DONE;
}

/**
 * A finished card that gets the full-screen reward (R): today newly lit (the day's first card), a perfect day, or a
 * level-up. Any other card gets the light celebration (stamp in place, +points, her line, Undo) (phone v2, 11-03).
 */
export function isBigReward(r: Pick<Reward, 'streakTo' | 'perfect' | 'levelFrom' | 'levelTo'>): boolean {
  return r.streakTo != null || !!r.perfect || r.levelTo > r.levelFrom;
}

export function nextFlameIn(streak: number): number | null {
  const next = FLAME_STEPS.find((s) => s > streak);
  return next == null ? null : next - streak;
}
export const flameScale = (streak: number) => (streak >= 100 ? 1.1 : streak >= 30 ? 1 : streak >= 7 ? 0.9 : 0.8);
export const flameGrewAt = (streak: number) => FLAME_STEPS.includes(streak);

/** Closeness meter from days together with a buddy: Just met, Getting to know you, Friends, Good friends, Close. */
export const CLOSENESS = ['Just met', 'Getting to know you', 'Friends', 'Good friends', 'Close'];
export const closenessOf = (d: number): 0 | 1 | 2 | 3 | 4 => (d >= 100 ? 4 : d >= 60 ? 3 : d >= 30 ? 2 : d >= 7 ? 1 : 0);

// ---------------------------------------------------------------- route (season 1)
/** One stop per level from level 2. `k` = keepsake slot (0-7, mapped through KORDER), `n` = chapter or stage number. */
export interface RouteDef { L: number; t: StopType; k?: number; n?: number; sound?: string; name?: string }
export const ROUTE: RouteDef[] = [
  { L: 2, t: 'keep', k: 0 }, { L: 3, t: 'chap', n: 2 }, { L: 4, t: 'place', n: 2 }, { L: 5, t: 'keep', k: 1 },
  { L: 6, t: 'chap', n: 3 }, { L: 7, t: 'place', n: 3 }, { L: 8, t: 'sound', sound: 'work-song', name: 'A song for working together' },
  { L: 9, t: 'chap', n: 4 }, { L: 10, t: 'keep', k: 2 }, { L: 11, t: 'place', n: 4 }, { L: 12, t: 'keep', k: 3 },
  { L: 13, t: 'chap', n: 5 }, { L: 14, t: 'sound', sound: 'rain', name: 'Rain sounds for working together' },
  { L: 15, t: 'place', n: 5 }, { L: 16, t: 'keep', k: 4 }, { L: 17, t: 'chap', n: 6 }, { L: 18, t: 'keep', k: 5 },
  { L: 19, t: 'place', n: 6 }, { L: 20, t: 'sound', sound: 'radio', name: 'Late-night radio for working together' },
  { L: 21, t: 'chap', n: 7 }, { L: 22, t: 'keep', k: 6 }, { L: 23, t: 'keep', k: 7 }, { L: 24, t: 'place', n: 7 },
];
/** Month-one build: stops up to and including this index have content. ROUTE[17] = L19 place 6. */
export const ROUTE_READY = 17;
export const SEASON_NEXT_LABEL = 'Something new next season';
export const COMING_SOON_LABEL = 'Coming soon';

/** Keepsake slot k -> index on the character's 4x2 keepsake sheet. */
export const KORDER: Record<CharId, number[]> = {
  sunny: [0, 6, 3, 5, 1, 2, 7, 4], margo: [3, 2, 6, 5, 1, 0, 4, 7],
  arjun: [3, 0, 4, 5, 2, 7, 6, 1], tavo: [4, 6, 3, 1, 0, 7, 5, 2],
};

/**
 * Build a buddy's route with absolute levels. A buddy who has reached `stops` stops while the user is at `level`
 * gets the next stop at level+1, the one after at level+2, and so on (one stop per level-up).
 */
export function buildRoute(ch: CharId, stops: number, level: number, labels: { chapter: (n: number) => string; placeName: string; keepName: (sheetIdx: number) => string }): RouteStop[] {
  return ROUTE.map((r, idx) => {
    const reached = idx < stops;
    const lvl = reached ? r.L : level + (idx - stops) + 1;
    const keep = r.t === 'keep' ? KORDER[ch][r.k!] : undefined;
    const label = r.t === 'chap' ? `Chapter ${r.n}: ${labels.chapter(r.n!)}`
      : r.t === 'place' ? `A new look for ${labels.placeName}`
      : r.t === 'keep' ? labels.keepName(keep!)
      : r.name!;
    return { idx, type: r.t, label, level: lvl, reached, ready: idx <= ROUTE_READY, keep, chap: r.t === 'chap' ? r.n : undefined, place: r.t === 'place' ? r.n : undefined, sound: r.sound };
  });
}

export const stageOfStops = (stops: number) => 1 + ROUTE.slice(0, stops).filter((r) => r.t === 'place').length;
export const chaptersOfStops = (stops: number) => 1 + ROUTE.slice(0, stops).filter((r) => r.t === 'chap').length;
export const keepsakesOfStops = (ch: CharId, stops: number) => ROUTE.slice(0, stops).filter((r) => r.t === 'keep').map((r) => KORDER[ch][r.k!]);
export const soundsOfStops = (stops: number) => ROUTE.slice(0, stops).filter((r) => r.t === 'sound').map((r) => r.sound!);
/** A level-up only opens a stop if that stop's content is ready in this build. */
export const canOpenStop = (stops: number) => stops <= ROUTE_READY;

// ---------------------------------------------------------------- time
export const DAY_ROLLOVER_HOURS = 4;
export const QUIET_START = 22 * 60; // 22:00
export const QUIET_END = 8 * 60;    // 08:00

/** Local wall-clock parts for an instant in a time zone. */
export function localParts(ms: number, tz: string) {
  const f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', weekday: 'short' });
  const p: Record<string, string> = {};
  for (const x of f.formatToParts(new Date(ms))) p[x.type] = x.value;
  return { y: +p.year, mo: +p.month, d: +p.day, h: +p.hour % 24, mi: +p.minute, s: +p.second, wd: p.weekday };
}

/** Day key (YYYY-MM-DD) of an instant: the local date of (instant - 4h). */
export function dayKey(ms: number, tz: string): string {
  const p = localParts(ms - DAY_ROLLOVER_HOURS * 3600000, tz);
  return `${p.y}-${String(p.mo).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`;
}

/** Minutes after local midnight of an instant. */
export function localMinutes(ms: number, tz: string): number { const p = localParts(ms, tz); return p.h * 60 + p.mi; }

/** Epoch ms of local wall time `minutes` on calendar date `day` (YYYY-MM-DD) in `tz`. DST-safe to the minute. */
export function atLocal(day: string, minutes: number, tz: string): number {
  const [y, mo, d] = day.split('-').map(Number);
  let guess = Date.UTC(y, mo - 1, d, Math.floor(minutes / 60), minutes % 60);
  for (let i = 0; i < 3; i++) {
    const p = localParts(guess, tz);
    const asUtc = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi);
    const want = Date.UTC(y, mo - 1, d, Math.floor(minutes / 60), minutes % 60);
    guess += want - asUtc;
  }
  return guess;
}

export const addDays = (day: string, n: number): string => {
  const [y, mo, d] = day.split('-').map(Number);
  const t = new Date(Date.UTC(y, mo - 1, d + n));
  return t.toISOString().slice(0, 10);
};
export const daysBetween = (a: string, b: string): number => Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86400000);
/** ISO weekday 1 (Mon) .. 7 (Sun) of a day key. */
export const isoWeekday = (day: string): number => { const w = new Date(day + 'T12:00:00Z').getUTCDay(); return w === 0 ? 7 : w; };
export const weekStart = (day: string): string => addDays(day, 1 - isoWeekday(day));

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WD = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const weekdayName = (day: string) => WD[isoWeekday(day) - 1];
export const shortDate = (day: string) => { const [, mo, d] = day.split('-').map(Number); return `${MON[mo - 1]} ${d}`; };        // 'Nov 10'
export const dateLabel = (day: string) => `${weekdayName(day)}, ${shortDate(day)}`;                                              // 'Tuesday, Nov 10'
export const longDate = (day: string) => { const [, mo, d] = day.split('-').map(Number); return `${weekdayName(day)}, ${MONTH[mo - 1]} ${d}`; }; // lock screen
export const stampDate = (day: string) => shortDate(day).toUpperCase();                                                         // 'NOV 10'

/** '9:30 AM' -> 570. Accepts '9:30 AM', '9 AM', '21:30', '9:30am'. Returns null if unparseable. */
export function parseTime(s: string): number | null {
  const m = String(s).trim().match(/^(\d{1,2})(?::(\d{2}))?\s*([ap]\.?m\.?)?$/i);
  if (!m) return null;
  let h = +m[1]; const mi = m[2] ? +m[2] : 0; const ap = m[3]?.toLowerCase()[0];
  if (mi > 59 || h > 23) return null;
  if (ap === 'p' && h < 12) h += 12;
  if (ap === 'a' && h === 12) h = 0;
  return h * 60 + mi;
}
/** 570 -> '9:30 AM' */
export function fmtTime(min: number): string {
  const m = ((Math.round(min) % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60), mi = m % 60, ap = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(mi).padStart(2, '0')} ${ap}`;
}
export const inQuiet = (min: number) => min >= QUIET_START || min < QUIET_END;

/** Where "Later" moves a card. Returns the new local minutes and whether it moves to tomorrow. */
export function laterTarget(when: LaterWhen, nowMin: number): { at: number; tomorrow: boolean } {
  const round5 = (x: number) => Math.ceil(x / 5) * 5;
  switch (when) {
    case 'In 1 hour': { const at = round5(nowMin + 60); return at >= 22 * 60 ? { at: 9 * 60 + 30, tomorrow: true } : { at, tomorrow: false }; }
    case 'After lunch': return nowMin < 13 * 60 ? { at: 13 * 60 + 30, tomorrow: false } : { at: 13 * 60 + 30, tomorrow: true };
    case 'Tonight': return nowMin < 19 * 60 + 30 ? { at: 20 * 60, tomorrow: false } : { at: 9 * 60 + 30, tomorrow: true };
    default: return { at: 9 * 60 + 30, tomorrow: true };
  }
}

/** Short task name: the part of a card title before ':' ('Q3 report: write the next part' -> 'Q3 report'). */
export const taskShort = (title: string) => title.split(':')[0].trim();
